import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import InvoiceTemplate from './BillTemplates/InvoiceTemplate';
import InvoiceTemplateA5 from './BillTemplates/InvoiceTemplateA5';
import SimpleSalesTemplate from './BillTemplates/SimpleSalesTemplate';
import ModernSalesTemplate from './BillTemplates/ModernSalesTemplate';
import ChallanTemplate from './BillTemplates/ChallanTemplate';
import ChallanTemplateA5 from './BillTemplates/ChallanTemplateA5';
import QuotationTemplate from './BillTemplates/QuotationTemplate';
import QuotationTemplateColored from './BillTemplates/QuotationTemplateColored';
import PurchaseBillTemplate from './BillTemplates/PurchaseBillTemplate';
import PurchaseOrderTemplate from './BillTemplates/PurchaseOrderTemplate';
import ReceiptTemplate from './BillTemplates/ReceiptTemplate';
import PurchaseReceiptTemplate from './BillTemplates/PurchaseReceiptTemplate';
import { doc, onSnapshot } from '../firebase.config';
import { useEnhancedCompany } from '../contexts/EnhancedCompanyContext';
import { normalizeCompanyDocSnapshot } from '../utils/dataAdapter';
import { withMPINProtection } from './MPINProtectedRoute';
import { BILL_PREVIEW_SCREEN_CSS } from './BillTemplates/billPreviewScreenCss';
import AppSelect, { APP_SELECT_FILTER_TRIGGER } from './shared/AppSelect';

const PAGE_SIZES = [
  { key: 'a4', label: 'A4 (210×297mm)', width: 210, height: 297 },
  { key: 'a5', label: 'A5 (148×210mm)', width: 148, height: 210 },
  { key: 'letter', label: 'Letter (8.5×11in)', width: 216, height: 279 },
];
const ORIENTATIONS = [
  { key: 'portrait', label: 'Portrait' },
  { key: 'landscape', label: 'Landscape' },
];
const TEMPLATES = [
  { key: 'InvoiceTemplate', label: 'A4 Size Design - Standard Invoice', component: InvoiceTemplate },
  { key: 'InvoiceTemplateA5', label: 'A5 Size Design - Compact Invoice', component: InvoiceTemplateA5 },
  { key: 'SimpleSalesTemplate', label: 'Simple Sales Template - Compact & Clear', component: SimpleSalesTemplate },
  { key: 'ModernSalesTemplate', label: 'Modern Sales Template - Premium Design', component: ModernSalesTemplate },
  { key: 'ChallanTemplate', label: 'A4 Size Design - Challan', component: ChallanTemplate },
  { key: 'ChallanTemplateA5', label: 'A5 Size Design - Compact Challan', component: ChallanTemplateA5 },
  { key: 'QuotationTemplate', label: 'Quotation - Standard', component: QuotationTemplate },
  { key: 'QuotationTemplateColored', label: 'Quotation - Modern Colored', component: QuotationTemplateColored },
  { key: 'PurchaseOrderTemplate', label: 'Purchase Order', component: PurchaseOrderTemplate },
  { key: 'PurchaseBillTemplate', label: 'Purchase Bill', component: PurchaseBillTemplate },
  { key: 'ReceiptTemplate', label: 'Payment Receipt', component: ReceiptTemplate },
  { key: 'PurchaseReceiptTemplate', label: 'Purchase Receipt', component: PurchaseReceiptTemplate },
];

const BILL_TYPES = [
  { key: 'invoice', label: 'Invoice', templateKey: 'InvoiceTemplate' },
  { key: 'invoiceA5', label: 'Invoice (A5)', templateKey: 'InvoiceTemplateA5' },
  { key: 'challan', label: 'Challan', templateKey: 'ChallanTemplate' },
  { key: 'challanA5', label: 'Challan (A5)', templateKey: 'ChallanTemplateA5' },
  { key: 'quotation', label: 'Quotation', templateKey: 'QuotationTemplate' },
  { key: 'purchaseOrder', label: 'Purchase Order', templateKey: 'PurchaseOrderTemplate' },
  { key: 'purchaseBill', label: 'Purchase Bill', templateKey: 'PurchaseBillTemplate' },
  { key: 'receipt', label: 'Payment Receipt', templateKey: 'ReceiptTemplate' },
  { key: 'purchaseReceipt', label: 'Purchase Receipt', templateKey: 'PurchaseReceiptTemplate' },
];

// Sample/blurred data for preview
const sampleBill = {
  invoiceNumber: 'XXX-XXX/XXXX',
  date: 'YYYY-MM-DD',
  items: [
    {
      description: 'Sample Item Description',
      hsn: 'XXXX',
      qty: 'XX',
      rate: 'XXX',
      amount: 'XXXX',
      gst: 'XX',
      total: 'XXXX',
    },
    {
      description: 'Sample Item 2',
      hsn: 'XXXX',
      qty: 'XX',
      rate: 'XXX',
      amount: 'XXXX',
      gst: 'XX',
      total: 'XXXX',
    },
  ],
  discountType: 'percent',
  discountValue: 10,
  paymentLink: '',
  totals: {
    taxable: 'XXXX',
    sgst: 'XX',
    cgst: 'XX',
    igst: 'XX',
    grandTotal: 'XXXX',
  },
  gstTable: [
    { percent: 'XX', taxable: 'XXXX', sgst: 'XX', cgst: 'XX', igst: 'XX', total: 'XX' },
  ],
  terms: 'Sample terms and conditions...',
  paymentStatus: 'Unpaid',
  totalPaid: 0,
  remainingDue: 'XXXX',
};
const sampleParty = {
  name: 'XXX Party Name',
  address: 'XXX Address',
  gstin: 'XXGSTINXXXX',
  contact: 'XXXXXXXXXX',
};
const samplePayments = [
  { amount: 'XXXX', date: 'YYYY-MM-DD', mode: 'XXX', notes: 'XXX' },
];
const sampleReceipt = {
  amount: '5000',
  date: '2024-04-10',
  mode: 'UPI',
  reference: 'TXN123456',
  notes: 'Sample payment for invoice',
};

function BillTemplates({ db, userId, isAuthReady, appId }) {
  const [pageSize, setPageSize] = useState('a4');
  const [orientation, setOrientation] = useState('portrait');
  const [selectedTemplates, setSelectedTemplates] = useState({
    invoice: 'InvoiceTemplate',
    challan: 'ChallanTemplate',
    quotation: 'QuotationTemplate',
    purchaseOrder: 'PurchaseOrderTemplate',
    purchaseBill: 'PurchaseBillTemplate',
  });
  const [activeTab, setActiveTab] = useState('invoice');
  const [defaultTemplates, setDefaultTemplates] = useState({});
  const { currentCompany } = useEnhancedCompany();
  const [companyDetails, setCompanyDetails] = useState(null);

  useEffect(() => {
    // Load default templates from localStorage
    const savedDefaults = localStorage.getItem('defaultBillTemplates');
    if (savedDefaults) {
      try {
        const parsed = JSON.parse(savedDefaults);
        setDefaultTemplates(parsed);
        // Apply defaults to selectedTemplates
        setSelectedTemplates(prev => {
          const updated = { ...prev };
          Object.keys(parsed).forEach(key => {
            if (parsed[key]) {
              updated[key] = parsed[key];
            }
          });
          return updated;
        });
      } catch (e) {
        console.error('Error loading default templates:', e);
      }
    }
  }, []);

  useEffect(() => {
    const cid = currentCompany?.companyId || currentCompany?.id;
    if (!db || !isAuthReady || !cid) return;
    const companyDocRef = doc(db, 'companies', cid);
    const unsubscribe = onSnapshot(companyDocRef, (docSnap) => {
      if (docSnap.exists()) {
        setCompanyDetails(normalizeCompanyDocSnapshot(docSnap.data() || {}, cid));
      }
    });
    return () => unsubscribe();
  }, [db, isAuthReady, currentCompany]);

  const setAsDefault = (billType) => {
    const newDefaults = {
      ...defaultTemplates,
      [billType]: selectedTemplates[billType]
    };
    setDefaultTemplates(newDefaults);
    localStorage.setItem('defaultBillTemplates', JSON.stringify(newDefaults));
    alert(`Default ${BILL_TYPES.find(t => t.key === billType)?.label || billType} template set to ${TEMPLATES.find(t => t.key === selectedTemplates[billType])?.label || selectedTemplates[billType]}`);
  };

  const renderTemplateSection = (billType) => {
    const TemplateComponent = TEMPLATES.find(t => t.key === selectedTemplates[billType.key])?.component || InvoiceTemplate;
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-bold mb-4 text-center">{billType.label} Template</h2>
        <div className="mb-4 text-center text-sm text-gray-600">
          <strong>A4 Size Design:</strong> Standard layout with larger fonts and spacing | <strong>A5 Size Design:</strong> Compact layout optimized for smaller pages
        </div>
        <div className="mb-4 flex flex-wrap gap-4 items-center justify-center">
          <div className="flex gap-2 items-center">
            <label className="font-medium">Template:</label>
            <AppSelect
              value={selectedTemplates[billType.key]}
              onChange={e => setSelectedTemplates(st => ({ ...st, [billType.key]: e.target.value }))}
              aria-label={`${billType.label} template`}
              sheetTitle="Template"
              options={TEMPLATES.filter(t => {
                // For invoice, show Invoice, Simple Sales, Modern Sales, and A5 versions
                if (billType.key === 'invoice') {
                  return t.key.includes('Invoice') || t.key.includes('SimpleSales') || t.key.includes('ModernSales');
                }
                // For challan, show A4 and A5 versions
                if (billType.key === 'challan') {
                  return t.key.includes('Challan');
                }
                // For quotation, show both standard and colored versions
                if (billType.key === 'quotation') {
                  return t.key.includes('Quotation');
                }
                // For other templates, show only the specific template
                return t.key === billType.templateKey || t.key === 'InvoiceTemplate';
              }).map(t => ({ value: t.key, label: t.label }))}
              triggerClassName={`${APP_SELECT_FILTER_TRIGGER} w-auto min-w-[10rem] border rounded px-2 py-1`}
            />
            <button
              onClick={() => setAsDefault(billType.key)}
              className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors flex items-center gap-1"
              title="Set as default template"
            >
              {defaultTemplates[billType.key] === selectedTemplates[billType.key] ? (
                <>
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Default
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Set Default
                </>
              )}
            </button>
          </div>
          <div className="flex gap-2 items-center">
            <label className="font-medium">Page Size:</label>
            <AppSelect
              value={pageSize}
              onChange={e => setPageSize(e.target.value)}
              aria-label="Page size"
              sheetTitle="Page size"
              options={PAGE_SIZES.map(size => ({ value: size.key, label: size.label }))}
              triggerClassName={`${APP_SELECT_FILTER_TRIGGER} w-auto min-w-[10rem] border rounded px-2 py-1`}
            />
          </div>
          <div className="flex gap-2 items-center">
            <label className="font-medium">Orientation:</label>
            <AppSelect
              value={orientation}
              onChange={e => setOrientation(e.target.value)}
              aria-label="Orientation"
              sheetTitle="Orientation"
              options={ORIENTATIONS.map(o => ({ value: o.key, label: o.label }))}
              triggerClassName={`${APP_SELECT_FILTER_TRIGGER} w-auto min-w-[8rem] border rounded px-2 py-1`}
            />
          </div>
        </div>
        <div className="text-center text-xs text-gray-500 mb-2 px-2">
          On mobile, preview scales to fit your screen. Desktop shows print size at 100% zoom.
        </div>
        <div className="flex items-start justify-center pb-12 px-2 sm:px-4 overflow-x-hidden w-full">
          <div
            className="invoice-preview-page-shell w-full max-w-full lg:max-w-[210mm] p-2 sm:p-4 bg-white rounded-lg shadow-sm"
            data-acctoo-bill-preview="true"
          >
            {companyDetails ? (
              <TemplateComponent
                billData={sampleBill}
                invoiceData={sampleBill}
                companyDetails={{ ...companyDetails, gstinType: companyDetails.gstinType || '' }}
                partyDetails={sampleParty}
                bankDetails={{
                  name: companyDetails.bankName,
                  account: companyDetails.bankAccount,
                  ifsc: companyDetails.bankIfsc,
                  upi: companyDetails.upiId,
                  upiQrUrl: companyDetails.upiQrUrl,
                  paymentGatewayLink: companyDetails.paymentGatewayLink,
                  sealUrl: companyDetails.sealUrl,
                  signUrl: companyDetails.signUrl,
                }}
                payments={samplePayments}
                pageSize={pageSize}
                orientation={orientation}
                previewMode={true}
                showPlaceholders={true}
              />
            ) : (
              <div className="text-gray-400 text-center text-lg mt-12">Loading company details...</div>
            )}
            <style>{BILL_PREVIEW_SCREEN_CSS}</style>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100 overflow-auto flex flex-col">
      <h1 className="text-3xl font-bold mb-4 mt-8 text-center">Bill Templates</h1>
      <p className="text-center text-sm text-slate-600 mb-4 max-w-xl mx-auto px-4">
        Preview and set default print layouts for invoices, challans, quotations, and more.
      </p>
      <div className="max-w-4xl mx-auto w-full px-2 sm:px-4 mb-6">
        <Link
          to="/bill-templates/dummy-bill"
          className="block rounded-lg border border-amber-200 bg-amber-50 hover:bg-amber-100/80 transition-colors p-4 sm:p-5"
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="font-semibold text-slate-900">Dummy Bill</h2>
              <p className="text-sm text-slate-600 mt-1 max-w-2xl">
                Create a layout-only bill with any date or number, load from an existing invoice, and print or export PDF.
                Not saved to sales, stock, or accounts.
              </p>
            </div>
            <span className="text-sm font-medium text-amber-900 bg-white border border-amber-200 rounded-md px-3 py-1.5 shrink-0">
              Open →
            </span>
          </div>
        </Link>
      </div>
      <div className="flex overflow-x-auto gap-2 mb-4 px-2 sm:px-0 max-w-full">
        {BILL_TYPES.map(tab => (
          <button
            key={tab.key}
            className={`shrink-0 px-4 py-2.5 rounded-t font-semibold focus:outline-none touch-manipulation min-h-[44px] ${activeTab === tab.key ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div className="max-w-4xl mx-auto w-full px-2 sm:px-4 pb-8">
        {activeTab === 'receipt' ? (
          <div className="p-4 bg-white rounded shadow">
            <ReceiptTemplate
              receipt={sampleReceipt}
              bill={sampleBill}
              company={companyDetails || { firmName: 'Sample Company', address: 'Sample Address', gstin: 'XXGSTINXXXX' }}
              party={sampleParty}
              receiptNumber={'PYR25-26/1'}
            />
          </div>
        ) : activeTab === 'purchaseReceipt' ? (
          <div className="p-4 bg-white rounded shadow">
            <PurchaseReceiptTemplate
              receipt={sampleReceipt}
              bill={sampleBill}
              company={companyDetails || { firmName: 'Sample Company', address: 'Sample Address', gstin: 'XXGSTINXXXX' }}
              party={sampleParty}
              receiptNumber={'PYR25-26/1'}
            />
          </div>
        ) : (
          renderTemplateSection(BILL_TYPES.find(b => b.key === activeTab))
        )}
      </div>
    </div>
  );
}

export default withMPINProtection(BillTemplates, 'Bill Templates');
